import plotzioTheme from "../../design/tokens/dist/plotzio/theme.json";
import meridianTheme from "../../design/tokens/dist/meridian-example/theme.json";
import { defaultBrandConfig } from "./brand.default";
import { applyBrandEnvOverrides } from "./brand";
import { BrandConfigSchema, type BrandConfig } from "./brand.schema";

/**
 * White-label THEME selection — the M1 bridge between the compiled design
 * tokens (design/tokens/dist/<name>/{tokens.css,theme.json}) and M0's brand
 * config module.
 *
 * One switch re-skins the whole app with zero code edits:
 *
 *   BRAND_THEME=meridian-example pnpm build
 *
 * Resolution order for brand values (each layer overrides the previous):
 *   1. src/config/brand.default.ts        — the M0 base source
 *   2. the selected theme's `brand` block — design/tokens/themes/<name>.json
 *      (compiled into dist/<name>/theme.json), mapped through M0's own
 *      `applyBrandEnvOverrides` merge so the two modules cannot drift
 *   3. process.env BRAND_* overrides      — per-deployment, always win
 *
 * The palette side of the same switch is CSS-only: every compiled theme's
 * tokens.css is scoped to [data-theme="<name>"], the root layout stamps
 * <html data-theme={getThemeName()}>, and the matching variables activate.
 */

/** Compiled theme artifact shape (dist/<name>/theme.json). */
export interface CompiledTheme {
  name: string;
  brand: {
    brandName: string;
    wordmark: { text: string; logoUrl: string | null };
    appDomain: string;
    supportEmail: string;
    legalEntity: string;
    // legalUrls/attribution/legal may carry only a subset (or be absent); the
    // rest falls through from brand.default.ts via the structured overlay below.
    legalUrls: Partial<BrandConfig["legalUrls"]>;
    attribution: Partial<BrandConfig["attribution"]>;
    legal?: Partial<BrandConfig["legal"]> & { effectiveDates?: Partial<BrandConfig["legal"]["effectiveDates"]> };
  };
  tokens: Record<string, unknown>;
}

export interface ThemeConfig {
  /** Theme id — also the value of <html data-theme>. */
  name: string;
  /** Wordmark contract (DESIGN_SYSTEM.md §12): text renders in type.family.display when logoUrl is null. */
  wordmark: { text: string; logoUrl: string | null };
  /** Fully-resolved brand config (base -> theme -> env), validated by M0's schema. */
  brand: BrandConfig;
  /** Flattened, alias-resolved token map ("color.accent.default" -> the theme's accent value). */
  tokens: Record<string, unknown>;
}

const COMPILED_THEMES: Record<string, CompiledTheme> = {
  [plotzioTheme.name]: plotzioTheme as CompiledTheme,
  [meridianTheme.name]: meridianTheme as CompiledTheme,
};

export const DEFAULT_THEME_NAME = "plotzio";

export function getAvailableThemeNames(): string[] {
  return Object.keys(COMPILED_THEMES);
}

/** Active theme name: BRAND_THEME env, validated against the compiled set. */
export function getThemeName(env: Record<string, string | undefined> = process.env): string {
  const name = env.BRAND_THEME ?? DEFAULT_THEME_NAME;
  if (!(name in COMPILED_THEMES)) {
    throw new Error(
      `Unknown BRAND_THEME "${name}" — compiled themes: ${getAvailableThemeNames().join(", ")}. ` +
        `Add design/tokens/themes/${name}.json and run "pnpm tokens:build".`,
    );
  }
  return name;
}

function stringToken(theme: CompiledTheme, path: string): string {
  const v = theme.tokens[path];
  if (typeof v !== "string") throw new Error(`Theme "${theme.name}" is missing string token "${path}"`);
  return v;
}

/**
 * Resolve a full ThemeConfig. `env` is injectable for tests; production
 * callers use the process environment.
 */
export function getTheme(name?: string, env: Record<string, string | undefined> = process.env): ThemeConfig {
  const themeName = name ?? getThemeName(env);
  const compiled = COMPILED_THEMES[themeName];
  if (!compiled) {
    throw new Error(`Unknown theme "${themeName}" — compiled themes: ${getAvailableThemeNames().join(", ")}`);
  }

  // Layer 1.5 — structured overlay for the block-shaped brand fields the flat
  // env mechanism can't express (the `legal` block, its per-document
  // effectiveDates map, and the extended legalUrls/attribution keys). A theme
  // supplies only what it re-skins; everything else falls through from the M0
  // default. Kept structural so per-document dates re-skin cleanly.
  const tb = compiled.brand;
  const baseWithTheme: BrandConfig = {
    ...defaultBrandConfig,
    legalUrls: { ...defaultBrandConfig.legalUrls, ...tb.legalUrls },
    attribution: { ...defaultBrandConfig.attribution, ...tb.attribution },
    legal: {
      ...defaultBrandConfig.legal,
      ...(tb.legal ?? {}),
      effectiveDates: { ...defaultBrandConfig.legal.effectiveDates, ...(tb.legal?.effectiveDates ?? {}) },
    },
  };

  // Layer 2 — the theme's flat brand fields, expressed as the same override
  // shape M0's env mechanism uses so both paths share one merge implementation.
  const themed = applyBrandEnvOverrides(baseWithTheme, {
    BRAND_NAME: compiled.brand.brandName,
    BRAND_LOGO_URL: compiled.brand.wordmark.logoUrl ?? undefined,
    BRAND_COLOR_PRIMARY: stringToken(compiled, "color.text.primary"),
    BRAND_COLOR_ACCENT: stringToken(compiled, "color.accent.default"),
    BRAND_APP_DOMAIN: compiled.brand.appDomain,
    BRAND_SUPPORT_EMAIL: compiled.brand.supportEmail,
    BRAND_LEGAL_ENTITY: compiled.brand.legalEntity,
    BRAND_LEGAL_URL_TOS: compiled.brand.legalUrls.tos,
    BRAND_LEGAL_URL_PRIVACY: compiled.brand.legalUrls.privacy,
    BRAND_ATTRIBUTION_BASE: compiled.brand.attribution.base,
    BRAND_ATTRIBUTION_OSM: compiled.brand.attribution.osm,
    BRAND_ATTRIBUTION_PROTOMAPS: compiled.brand.attribution.protomaps,
  });

  // Layer 3 — per-deployment env overrides still win.
  const merged = applyBrandEnvOverrides(themed, env);

  const parsed = BrandConfigSchema.safeParse(merged);
  if (!parsed.success) {
    throw new Error(`Invalid brand config for theme "${themeName}": ${parsed.error.message}`);
  }

  return {
    name: themeName,
    wordmark: compiled.brand.wordmark,
    brand: parsed.data,
    tokens: compiled.tokens,
  };
}
